import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { hasCapability } from "@/lib/permissions";
import {
  Plus, Search, ArrowRightLeft, Truck, Download, CheckCircle2, XCircle, Clock, PackageCheck, ArrowUpRight, ArrowDownLeft, LogIn,
} from "lucide-react";

const STATUS = {
  issued: { icon: Truck, cls: "bg-blue-50 text-blue-800 border-blue-200", label: "In Transit" },
  partially_received: { icon: PackageCheck, cls: "bg-teal-50 text-teal-800 border-teal-200", label: "Partial" },
  received: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Received" },
  rejected: { icon: XCircle, cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Rejected" },
  cancelled: { icon: XCircle, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelled" },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function fyStart() {
  const t = new Date();
  const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
  return `${y}-04-01`;
}

export default function MaterialTransfers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    direction: "", status: "", start_date: fyStart(), end_date: todayISO(), q: "",
  });
  const canRaise = hasCapability(user, "raise_material_transfer");
  const canGateIn = hasCapability(user, "gate_in_material_transfer");
  const isGateKeeper = user?.role === "gate_keeper" || user?.role === "logistics_supervisor";

  const pendingGateIn = useMemo(() => {
    return (rows || [])
      .filter((r) => !r.gate_in_status && (r.status === "issued" || r.status === "partially_received"))
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  }, [rows]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/material-transfers", { params }),
        api.get("/material-transfers/summary"),
      ]);
      setRows(list || []);
      setSummary(sum);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load transfers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.direction, filters.status, filters.start_date, filters.end_date]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.transfer_number} ${r.purpose} ${r.issued_by_name} ${(r.items || []).map((i) => `${i.description} ${i.im_code || ""}`).join(" ")}`.toLowerCase().includes(q),
    );
  }, [rows, filters.q]);

  const downloadXlsx = async () => {
    const token = localStorage.getItem("assetiq_token");
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== "q") params.set(k, v); });
    const res = await fetch(`${API_BASE}/material-transfers/export.xlsx?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `material_transfers.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const c = summary?.counts || {};
  return (
    <div className="space-y-6" data-testid="material-transfers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Logistics</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <ArrowRightLeft className="w-7 h-7" /> Material Transfer Register
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Office ↔ Factory movement log — with auto-generated Transfer &amp; Receipt Notes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadXlsx} data-testid="export-transfers-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          {canRaise && (
            <Button onClick={() => navigate("/material-transfers/new")} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="new-transfer-btn">
              <Plus className="w-4 h-4 mr-2" /> New Transfer
            </Button>
          )}
        </div>
      </div>

      {/* Pending Gate-In tile (visible to anyone with gate-in capability). */}
      {canGateIn && pendingGateIn.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="pending-gate-in-tile">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                  <LogIn className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Pending Gate-In</div>
                  <div className="text-[11px] text-slate-500">{pendingGateIn.length} shipment{pendingGateIn.length === 1 ? "" : "s"} awaiting arrival · oldest first</div>
                </div>
              </div>
              <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50">
                {pendingGateIn.length}
              </Badge>
            </div>
            <div className="grid gap-2 max-h-72 overflow-y-auto pr-1">
              {pendingGateIn.map((t) => {
                const days = t.transfer_date
                  ? Math.max(0, Math.floor((Date.now() - new Date(t.transfer_date).getTime()) / 86400000))
                  : 0;
                const dirLabel = t.direction === "office_to_factory" ? "HO → Factory" : "Factory → HO";
                return (
                  <button
                    key={t.id}
                    onClick={() => navigate(`/material-transfers/${t.id}`)}
                    className="text-left flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 hover:border-[#7C1D3A] hover:shadow-sm transition"
                    data-testid={`pending-gate-in-row-${t.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-slate-800">{t.transfer_number}</span>
                        <span className="text-[10px] text-slate-400">·</span>
                        <span className="text-[11px] text-slate-500">{dirLabel}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                        <span className="text-slate-700 font-medium">{t.issued_by_name}</span> · {t.items?.length || 0} item{(t.items?.length || 0) === 1 ? "" : "s"} · {t.purpose || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${
                          days >= 3 ? "bg-rose-100 text-rose-800" : days >= 1 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {days === 0 ? "Today" : `${days}d ago`}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {canGateIn && pendingGateIn.length === 0 && !loading && isGateKeeper && (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="pending-gate-in-empty">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">All clear</div>
              <div className="text-[11px] text-slate-500">No shipments awaiting gate-in right now.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={<Truck className="w-4 h-4" />} label="In Transit" value={c.issued || 0} tint="sky" />
        <Kpi icon={<PackageCheck className="w-4 h-4" />} label="Partial" value={c.partially_received || 0} tint="teal" />
        <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label="Received" value={c.received || 0} tint="emerald" />
        <Kpi icon={<XCircle className="w-4 h-4" />} label="Rejected" value={c.rejected || 0} tint="rose" />
        <Kpi icon={<Clock className="w-4 h-4" />} label="Cancelled" value={c.cancelled || 0} tint="slate" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Direction</label>
            <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} className="block w-52 h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="filter-direction">
              <option value="">All directions</option>
              <option value="office_to_factory">Office → Factory (HO/…)</option>
              <option value="factory_to_office">Factory → Office (FTR/…)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="block w-44 h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="filter-status">
              <option value="">All statuses</option>
              {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="w-40" data-testid="filter-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="w-40" data-testid="filter-end" />
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search #, purpose, material..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pl-9" data-testid="filter-search" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Transfer #</th>
                <th className="px-4 py-3 text-left">Direction</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Purpose · Items</th>
                <th className="px-4 py-3 text-left">Issued By</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No transfers match.</td></tr>
                : filtered.map((r) => {
                  const st = STATUS[r.status] || STATUS.issued;
                  const StIcon = st.icon;
                  const isOTF = r.direction === "office_to_factory";
                  const Arrow = isOTF ? ArrowUpRight : ArrowDownLeft;
                  return (
                    <tr key={r.id} onClick={() => navigate(`/material-transfers/${r.id}`)}
                        className="hover:bg-slate-50 cursor-pointer" data-testid={`transfer-row-${r.transfer_number}`}>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A]">{r.transfer_number}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs">
                          <Arrow className={`w-3.5 h-3.5 ${isOTF ? "text-blue-600" : "text-emerald-600"}`} />
                          <span>{r.from_location} → {r.to_location}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs">{r.transfer_date}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900 truncate max-w-md">{r.purpose}</div>
                        <div className="text-[11px] text-slate-500">{(r.items || []).length} item(s) · {(r.items || []).map((i) => i.description).slice(0, 2).join(", ")}{(r.items || []).length > 2 && "…"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">{r.issued_by_name}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={`gap-1 border ${st.cls}`}>
                            <StIcon className="w-3 h-3" /> {st.label}
                          </Badge>
                          {(r.status === "issued" || r.status === "partially_received") && !r.gate_in_status && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid={`awaiting-gate-in-${r.transfer_number}`}>
                              <LogIn className="w-3 h-3" /> Gate
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Kpi({ icon, label, value, tint }) {
  const tints = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    teal: "bg-teal-50 text-teal-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className={`${tints[tint]} rounded-lg p-3`}>
      <div className="flex items-center gap-2 text-[10px] uppercase font-semibold tracking-wider opacity-80">{icon}{label}</div>
      <div className="mt-1 text-2xl font-bold font-mono">{value}</div>
    </div>
  );
}
